<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" location="bottom start" @click.stop>
    <template #activator="{ props: menuProps }">
      <button
        v-bind="menuProps"
        type="button"
        class="tag-picker__add"
        :class="`tag-picker__add--${size}`"
        :aria-label="size === 'sm' ? 'Add tag' : undefined"
        @click.stop>
        <v-icon :size="size === 'md' ? 14 : 16">mdi-plus</v-icon>
        <span v-if="size === 'md'">Add tag</span>
      </button>
    </template>
    <v-card min-width="240" class="tag-picker__menu">
      <div class="tag-picker__search">
        <v-icon size="14" class="tag-picker__search-icon">mdi-magnify</v-icon>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="tag-picker__search-input"
          placeholder="Filter tags..."
          aria-label="Filter tags"
          @keydown="onSearchKeydown" />
      </div>
      <div ref="listEl" class="tag-picker__list">
        <div
          v-for="(def, index) in filteredTags"
          :key="def.key"
          :data-index="index"
          class="tag-picker__item"
          :class="{
            'tag-picker__item--disabled': def.auto,
            'tag-picker__item--active': index === highlightedIndex,
          }"
          @mousedown.prevent
          @mousemove="!def.auto && (highlightedIndex = index)"
          @click.stop="!def.auto && onToggle(def.key, !currentTags.includes(def.key))">
          <span class="tag-picker__dot" :style="{ background: TAG_COLORS[def.color] || TAG_COLORS.grey }" />
          <span class="tag-picker__label">{{ def.label }}</span>
          <span v-if="def.auto" class="tag-picker__auto">auto</span>
          <v-icon v-if="currentTags.includes(def.key)" size="16" color="accent" class="tag-picker__check">
            mdi-check
          </v-icon>
        </div>
        <div v-if="!filteredTags.length" class="tag-picker__empty">No tags match</div>
      </div>
    </v-card>
  </v-menu>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { filterUserTags } from "@/utils/tags";

const props = defineProps({
  tags: { type: Array, default: () => [] },
  tagDefs: { type: Array, default: () => [] },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:tags"]);

const menuOpen = ref(false);
const search = ref("");
const highlightedIndex = ref(-1);
const searchInput = ref(null);
const listEl = ref(null);

const currentTags = computed(() => props.tags || []);

const filteredTags = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.tagDefs;
  return props.tagDefs.filter((def) => (def.label || "").toLowerCase().includes(query));
});

watch(menuOpen, (open) => {
  if (open) {
    resetHighlight();
    nextTick(() =>
      requestAnimationFrame(() => {
        if (listEl.value) listEl.value.style.minHeight = `${listEl.value.offsetHeight}px`;
        searchInput.value?.focus();
        requestAnimationFrame(() => searchInput.value?.focus());
      })
    );
  } else {
    search.value = "";
    highlightedIndex.value = -1;
    listEl.value?.style.removeProperty("min-height");
  }
});

watch([search, () => props.tagDefs], resetHighlight);

function resetHighlight() {
  highlightedIndex.value = filteredTags.value.findIndex((def) => !def.auto);
}

function onSearchKeydown(event) {
  if (event.isComposing) return;
  const actions = {
    ArrowDown: () => moveHighlight(1),
    ArrowUp: () => moveHighlight(-1),
    Enter: toggleHighlighted,
    Escape: () => (menuOpen.value = false),
  };
  const action = actions[event.key];
  if (!action) return;
  event.preventDefault();
  event.stopPropagation();
  action();
}

function moveHighlight(step) {
  const items = filteredTags.value;
  if (!items.some((def) => !def.auto)) return;
  let index = highlightedIndex.value < 0 && step < 0 ? 0 : highlightedIndex.value;
  do {
    index = (index + step + items.length) % items.length;
  } while (items[index].auto);
  highlightedIndex.value = index;
  nextTick(() => listEl.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" }));
}

function toggleHighlighted() {
  const def = filteredTags.value[highlightedIndex.value];
  if (def && !def.auto) onToggle(def.key, !currentTags.value.includes(def.key));
}

function onToggle(key, checked) {
  const userTags = filterUserTags(currentTags.value, props.tagDefs);
  emit("update:tags", checked ? [...userTags, key] : userTags.filter((t) => t !== key));
}
</script>

<style lang="scss" scoped>
.tag-picker__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }

  &--sm {
    width: 26px;
    height: 26px;
    padding: 0;
    flex: 0 0 auto;
    border-radius: 50%;
  }

  &--md {
    height: 26px;
    padding: 0 12px 0 9px;
    border-radius: 13px;
  }
}

.tag-picker__menu {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  border-radius: 12px !important;
}

.tag-picker__search {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgba($lh_color_navy, 0.08);
}

.tag-picker__search-icon {
  color: $lh_color_blue_gray;
  flex: 0 0 auto;
}

.tag-picker__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: $lh_font_family_dm_sans;
  font-size: 13.5px;
  color: $lh_color_navy;

  &::placeholder {
    color: $lh_color_blue_gray;
  }
}

.tag-picker__list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 8px;
}

.tag-picker__empty {
  margin: auto;
  padding: 12px 10px;
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  color: $lh_color_blue_gray;
}

.tag-picker__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &--active {
    background: $lh_color_snowy_white;
  }

  &--disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.tag-picker__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.tag-picker__label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-picker__auto {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $lh_color_blue_gray;
}

.tag-picker__check {
  flex: 0 0 auto;
}
</style>
